/**
 * Outreach page — parse chat members into a pool, then run DM campaigns.
 *
 * A separate tool from the chat autoposter: it messages scraped users
 * privately. Mass DMs are the fastest way to get an account banned, so the page
 * leads with a warning and the campaign editor pushes conservative throttling.
 *
 * Route: /app/outreach
 */

import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Crown,
  Pause,
  Play,
  Plus,
  Radar,
  Send,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card, Input, Sheet } from "@/design";
import { useAccounts } from "@/api/accounts";
import { useChats } from "@/api/chats";
import { useTemplates } from "@/api/templates";
import {
  useCampaigns,
  useClearPool,
  useCreateCampaign,
  useDeleteCampaign,
  usePool,
  useScrapeChat,
  useStartCampaign,
  useStopCampaign,
  type Campaign,
} from "@/api/outreach";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

export function OutreachPage() {
  const navigate = useNavigate();
  const { data: pool } = usePool();
  const { data: campaigns } = useCampaigns();
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);

  const total = pool?.total ?? 0;
  const contacted = pool?.contacted ?? 0;

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate("/app");
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Парсинг + ЛС</div>
        <div className="size-10" />
      </div>

      {/* Ban-risk warning — DM outreach is the top account-ban vector. */}
      <div className="flex items-start gap-2.5 rounded-2xl bg-danger/[0.08] border border-danger/20 px-4 py-3">
        <AlertTriangle className="size-4 text-danger shrink-0 mt-0.5" />
        <div className="text-[12px] text-text-muted leading-relaxed">
          Рассылка в личные сообщения незнакомым людям — самый быстрый способ
          получить бан аккаунта. Используйте уникализацию текста (spintax),
          щадящие интервалы и малые дневные лимиты. Прогревайте новые аккаунты.
        </div>
      </div>

      {/* Pool card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
                Пул юзеров
              </div>
              <div className="text-[32px] leading-none font-semibold tabular-nums tracking-tight mt-1">
                {total.toLocaleString("ru")}
              </div>
              <div className="text-[11px] text-text-muted mt-1">
                отправлено: {contacted.toLocaleString("ru")}
              </div>
            </div>
            <div className="size-14 rounded-2xl bg-accent-soft grid place-items-center">
              <Users className="size-6 text-accent" />
            </div>
          </div>
          <Button
            fullWidth
            leftIcon={<Radar />}
            onClick={() => {
              haptic.select();
              setScrapeOpen(true);
            }}
          >
            Спарсить участников чата
          </Button>
        </Card>
      </motion.div>

      {/* Campaigns */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium flex items-center gap-1.5">
            <Send className="size-3" />
            ЛС-кампании
          </div>
          <button
            onClick={() => {
              haptic.select();
              setCampaignOpen(true);
            }}
            className="text-xs text-accent hover:text-accent/80 flex items-center gap-0.5 transition-colors"
          >
            <Plus className="size-3.5" />
            Кампания
          </button>
        </div>

        {(campaigns?.length ?? 0) === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-xs text-text-muted">
            Нет кампаний. Создайте кампанию с шаблоном, чтобы начать рассылку по
            пулу.
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns!.map((c) => (
              <CampaignCard key={c.id} campaign={c} poolTotal={total} />
            ))}
          </div>
        )}
      </div>

      <ScrapeSheet open={scrapeOpen} onClose={() => setScrapeOpen(false)} />
      <CampaignSheet open={campaignOpen} onClose={() => setCampaignOpen(false)} />
    </div>
  );
}

function CampaignCard({ campaign, poolTotal }: { campaign: Campaign; poolTotal: number }) {
  const start = useStartCampaign();
  const stop = useStopCampaign();
  const del = useDeleteCampaign();
  const running = campaign.status === "running";

  const handleToggle = async () => {
    haptic.impact("medium");
    try {
      if (running) {
        await stop.mutateAsync(campaign.id);
        toast.success("Кампания остановлена");
      } else {
        await start.mutateAsync(campaign.id);
        toast.success("Кампания запущена");
      }
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось");
      haptic.notify("error");
    }
  };

  const handleDelete = async () => {
    haptic.impact("medium");
    try {
      await del.mutateAsync(campaign.id);
      toast.success("Кампания удалена");
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось удалить");
    }
  };

  const progress = poolTotal > 0 ? Math.min(100, Math.round((campaign.sent_count / poolTotal) * 100)) : 0;

  return (
    <Card padded={false} className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-semibold truncate">
            {campaign.name || `Кампания #${campaign.id}`}
          </div>
          <div className="text-[11px] text-text-muted truncate mt-0.5">
            {campaign.template_name ?? "нет шаблона"} · лимит{" "}
            {campaign.daily_limit_per_account}/день · {campaign.min_interval_seconds}–
            {campaign.max_interval_seconds}с
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleToggle}
            disabled={start.isPending || stop.isPending}
            className={cn(
              "size-8 rounded-lg grid place-items-center transition-colors",
              running
                ? "bg-danger/15 text-danger hover:bg-danger/25"
                : "bg-success/15 text-success hover:bg-success/25",
            )}
            aria-label={running ? "Остановить" : "Запустить"}
          >
            {running ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
          </button>
          <button
            onClick={handleDelete}
            className="size-8 rounded-lg grid place-items-center text-text-dim hover:text-danger hover:bg-danger/10 transition-colors"
            aria-label="Удалить"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px]">
          <span className={running ? "text-success" : "text-text-dim"}>
            {running ? "идёт рассылка" : campaign.status === "paused" ? "на паузе" : "черновик"}
          </span>
          <span className="text-text-muted tabular-nums">
            {campaign.sent_count.toLocaleString("ru")} отправлено
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full bg-gradient-accent rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </Card>
  );
}

function ScrapeSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: chats } = useChats();
  const { data: accounts } = useAccounts();
  const scrape = useScrapeChat();
  const clearPool = useClearPool();
  const [chatId, setChatId] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);

  const handleScrape = async () => {
    if (chatId == null) {
      toast.error("Выберите чат");
      return;
    }
    haptic.impact("medium");
    try {
      const res = await scrape.mutateAsync({ chat_id: chatId, account_id: accountId });
      toast.success(`Добавлено ${res.scraped}, пропущено ${res.skipped}. Пул: ${res.pool_size}`);
      onClose();
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось спарсить");
      haptic.notify("error");
    }
  };

  const handleClear = async () => {
    haptic.impact("medium");
    try {
      await clearPool.mutateAsync();
      toast.success("Пул очищен");
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось очистить");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Парсинг участников"
      description="Соберём участников чата в пул для ЛС-рассылки"
    >
      <div className="space-y-5">
        <Field label="Чат (аккаунт должен быть участником)">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] max-h-[220px] overflow-y-auto divide-y divide-white/[0.04]">
            {(chats ?? []).length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-text-muted">
                Сначала добавьте чаты на вкладке «Чаты».
              </div>
            ) : (
              (chats ?? []).map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    haptic.select();
                    setChatId(c.id);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                    chatId === c.id ? "bg-accent-soft" : "hover:bg-white/[0.03]",
                  )}
                >
                  <div
                    className={cn(
                      "size-4 rounded-full border grid place-items-center shrink-0",
                      chatId === c.id ? "border-accent" : "border-white/20",
                    )}
                  >
                    {chatId === c.id && <div className="size-2 rounded-full bg-accent" />}
                  </div>
                  <span className="text-[13px] truncate">{c.title}</span>
                </button>
              ))
            )}
          </div>
        </Field>

        <Field label="Аккаунт для парсинга">
          <div className="flex flex-wrap gap-2">
            <Chip active={accountId === null} onClick={() => { haptic.select(); setAccountId(null); }}>
              Авто
            </Chip>
            {(accounts ?? [])
              .filter((a) => !a.is_banned)
              .map((a) => (
                <Chip
                  key={a.id}
                  active={accountId === a.id}
                  onClick={() => { haptic.select(); setAccountId(a.id); }}
                >
                  {a.name}
                </Chip>
              ))}
          </div>
        </Field>

        <Button fullWidth size="lg" leftIcon={<Radar />} onClick={handleScrape} loading={scrape.isPending}>
          Спарсить
        </Button>
        <Button variant="ghost" fullWidth onClick={handleClear} loading={clearPool.isPending}>
          Очистить пул
        </Button>
      </div>
    </Sheet>
  );
}

function CampaignSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: templates } = useTemplates();
  const { data: accounts } = useAccounts();
  const create = useCreateCampaign();
  const mailTemplates = (templates ?? []).filter((t) => t.kind === "mail");

  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [minInterval, setMinInterval] = useState(90);
  const [maxInterval, setMaxInterval] = useState(240);
  const [dailyLimit, setDailyLimit] = useState(20);

  const toggleAccount = (id: number) => {
    haptic.select();
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (templateId == null) {
      toast.error("Выберите шаблон");
      return;
    }
    if (maxInterval < minInterval) {
      toast.error("Максимальный интервал меньше минимального");
      return;
    }
    haptic.impact("medium");
    try {
      await create.mutateAsync({
        name,
        template_id: templateId,
        account_ids: [...selectedAccounts],
        min_interval_seconds: minInterval,
        max_interval_seconds: maxInterval,
        daily_limit_per_account: dailyLimit,
      });
      toast.success("Кампания создана");
      onClose();
      setName("");
      setTemplateId(null);
      setSelectedAccounts(new Set());
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось создать");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Новая ЛС-кампания"
      description="Шаблон + аккаунты + интервалы. Текст уникализируется через spintax."
    >
      <div className="space-y-5">
        <Field label="Название">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Оффер по лидам" />
        </Field>

        <Field label="Шаблон (текст сообщения)">
          <div className="flex flex-wrap gap-2">
            {mailTemplates.map((t) => (
              <Chip
                key={t.id}
                active={templateId === t.id}
                onClick={() => { haptic.select(); setTemplateId(templateId === t.id ? null : t.id); }}
              >
                {t.name}
              </Chip>
            ))}
            {mailTemplates.length === 0 && (
              <div className="text-[12px] text-text-muted">
                Нет шаблонов — создайте на вкладке «Шаблоны». Используйте spintax{" "}
                <code className="text-accent">{"{привет|здравствуйте}"}</code> для уникализации.
              </div>
            )}
          </div>
        </Field>

        <Field label="Аккаунты (пусто = все включённые)">
          <div className="flex flex-wrap gap-2">
            {(accounts ?? [])
              .filter((a) => !a.is_banned)
              .map((a) => (
                <Chip key={a.id} active={selectedAccounts.has(a.id)} onClick={() => toggleAccount(a.id)}>
                  {a.name}
                </Chip>
              ))}
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Мин, с">
            <Input type="number" min={30} value={minInterval} onChange={(e) => setMinInterval(Math.max(30, Number(e.target.value) || 30))} />
          </Field>
          <Field label="Макс, с">
            <Input type="number" min={30} value={maxInterval} onChange={(e) => setMaxInterval(Math.max(30, Number(e.target.value) || 30))} />
          </Field>
          <Field label="Лимит/день">
            <Input type="number" min={1} max={200} value={dailyLimit} onChange={(e) => setDailyLimit(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
        </div>

        <div className="flex items-start gap-2 px-1">
          <Crown className="size-3 text-text-dim shrink-0 mt-0.5" />
          <div className="text-[11px] text-text-dim leading-relaxed">
            Меньше — безопаснее. 15–25 ЛС в день на аккаунт и интервал от 90с
            заметно снижают риск бана.
          </div>
        </div>

        <Button fullWidth size="lg" onClick={handleCreate} loading={create.isPending}>
          Создать кампанию
        </Button>
      </div>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-text-muted font-medium px-1">{label}</div>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-2 rounded-xl text-[13px] font-medium transition-colors",
        active
          ? "bg-gradient-accent text-white"
          : "bg-white/[0.04] text-text-muted hover:bg-white/[0.07]",
      )}
    >
      {children}
    </button>
  );
}
